import * as React from 'react';
import { Link } from 'react-router-dom';
import { directRentalActions } from '../directRentalActions';
import { ILumoOneAppState, useAppSelector } from '../../boot/configureStore';
import { connect, useDispatch } from 'react-redux';
import { ApartmentAM } from '../../Apartments/Api/ApartmentAM';
import { ILocationDataProviderState } from '../../LocationDataProvider/ILocationDataProviderState';
import { routeFromRoot, routes } from '../../Common/routes';
import { LoadingSpinner } from '../../Components/LoadingSpinner';
import { ApiOperationState } from '../../Common/Api/ApiOperationState';
import { ErrorMessageDisplay } from '../../Components/ErrorMessageDisplay';
import * as QueryString from 'query-string';
import {
	ResourceText,
	IUseResourceTexts,
	withResourceTextSupport,
	useResource,
	useLanguage,
} from '../../Components/ResourceText';
import { layoutActions } from '../../Common/Layout/layoutActions';
import { BreadCrumbItem } from '../../Common/Layout/layoutState';
import { GetUrlForApartmentByLocationData } from '../../Apartments/Page/apartmentPageUrlHandling';
import { LocationData } from '../../LocationDataProvider/LocationData';
import { WaitingButton } from '../../Components/WaitingButton';
import PageMeta from '../../Common/Layout/PageMeta';
import { pageViewTelemetryActions } from '../../Telemetry/pageViewTelemetryActions';
import { Language } from '../../Common/interfaces/language';
import { ResourceImage } from '../../Components/ResourceImage';
import { ApartmentSalesChannel } from '../../Apartments/Api/ApartmentSearchResultAM';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useRunOnce } from '../../SSR/inlineRenderDispatchHelper';
import { addTelemetryEventForStep } from '../Rental/telemetry';

interface IDirectRentalWayfinderStateProps {
	isApartmentSelected: boolean;
	selectedApartmentId?: string;
	selectedApartment?: ApartmentAM;
	locationData: ILocationDataProviderState;
	language: Language;
	contactRequestStartProcessingState: ApiOperationState | undefined;
}

interface IDirectRentalWayfinderDispatchProps {
	getApartmentById: typeof directRentalActions.getApartmentById.request;
	setLayoutValues: typeof layoutActions.setValues;
	prepareNewRental: typeof directRentalActions.prepareNewRental;
	pageComplete: typeof pageViewTelemetryActions.pageComplete;
}

type IDirectRentalWayfinderProps = IDirectRentalWayfinderStateProps &
	IDirectRentalWayfinderDispatchProps &
	IUseResourceTexts;

const directRentalTabId = 'directRentalTab';
const contactRequestTabId = 'contactRequestTab';
const contactRequestTabIndex = 1;

export function getBreadcrumbParents(
	apartment: ApartmentAM | undefined,
	locationData: LocationData,
	language: Language,
): BreadCrumbItem[] {
	return apartment ? [getApartmentBreadcrumbItem(apartment, locationData, language)] : [];
}

function getApartmentBreadcrumbItem(
	apartment: ApartmentAM,
	locationData: LocationData,
	language: Language,
): BreadCrumbItem {
	return {
		name: apartment.streetAddress!,
		path: GetUrlForApartmentByLocationData(language, apartment, locationData),
	};
}

interface WithActiveTab {
	activeTab: number;
	setActiveTab: (tab: number) => void;
}

function DirectRentalWayfinder(props: IDirectRentalWayfinderProps) {
	const location = useLocation();
	const query = QueryString.parse(location.search);
	const [activeTab, setActiveTab] = useState(query.tab ? parseInt(query.tab.toString()) : 0);
	const navigate = useNavigate();
	const hasData = props.isApartmentSelected && !!props.selectedApartment;

	useEffect(() => {
		props.pageComplete({});
	}, []);

	useEffect(() => {
		if (!props.isApartmentSelected) {
			navigate(routeFromRoot()[props.language]);
		} else {
			props.prepareNewRental();
		}
	}, [props.isApartmentSelected]);

	useEffect(() => {
		if (!hasData && !!props.selectedApartmentId) {
			props.getApartmentById(props.selectedApartmentId);
		}
	}, [props.selectedApartmentId, hasData]);

	const setLayoutValues = () =>
		props.setLayoutValues({
			breadcrumbCurrentName: props.getResourceText('DirectRental_WayFinder_PageTitle'),
			breadcrumbParents: getBreadcrumbParents(
				props.selectedApartment,
				props.locationData.locationData!,
				props.language,
			),
			translateUrl: routeFromRoot(routes.startRenting),
		});

	useRunOnce(setLayoutValues);
	useEffect(() => {
		setLayoutValues();
	}, [props.selectedApartment, props.locationData.locationData, props.language]);

	useEffect(() => {
		if (
			props.selectedApartment?.channel === ApartmentSalesChannel.PersonalService &&
			activeTab !== contactRequestTabIndex
		) {
			setActiveTab(contactRequestTabIndex);
		}
	}, [props.selectedApartment?.channel]);

	useEffect(() => {
		if (props.contactRequestStartProcessingState === ApiOperationState.Succeeded) {
			navigate(routeFromRoot(routes.startRentingContactRequest)[props.language]);
		}
	}, [props.contactRequestStartProcessingState]);

	return (
		<>
			<PageMeta title={props.getResourceText('DirectRental_WayFinder_PageTitle')} />
			<div className="mod-rentFlow">
				<div className="mod-rentFlow__wrapper">
					{hasData ? <Wayfinder activeTab={activeTab} setActiveTab={setActiveTab} /> : <LoadingSpinner />}
				</div>
			</div>
		</>
	);
}

const mapStateToProps = ({
	locationData,
	directRental,
	language,
}: ILumoOneAppState): IDirectRentalWayfinderStateProps => {
	return {
		isApartmentSelected: !!directRental.selectedApartmentId,
		selectedApartmentId: directRental.selectedApartmentId,
		selectedApartment: directRental.currentApartment,
		locationData: locationData,
		language: language.currentLanguage,
		contactRequestStartProcessingState: directRental.contactRequestStartProcessingState,
	};
};

export default withResourceTextSupport(
	connect(mapStateToProps, {
		getApartmentById: directRentalActions.getApartmentById.request,
		startContactRequest: directRentalActions.startContactRequest.request,
		setLayoutValues: layoutActions.setValues,
		prepareNewRental: directRentalActions.prepareNewRental,
		pageComplete: pageViewTelemetryActions.pageComplete,
	})(DirectRentalWayfinder),
);

function Wayfinder({ activeTab, setActiveTab }: WithActiveTab) {
	return (
		<>
			<RentHeader activeTab={activeTab} />
			<Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
			<div className="mod-cardTabs__card -noGrid">
				<div className="mod-cardTabs__overFlow">
					<div className="mod-cardTabs__contentTabWrapper">
						{activeTab == 0 ? <DirectRental /> : <ContactRequest />}
					</div>
				</div>
			</div>
		</>
	);
}

function RentHeader({ activeTab }: Pick<WithActiveTab, 'activeTab'>) {
	const { getResourceText } = useResource();
	const { currentApartment, locationData } = useAppSelector(
		({ directRental: { currentApartment }, locationData: { locationData } }) => ({
			currentApartment,
			locationData,
		}),
	);

	if (!currentApartment) {
		return null;
	}

	const municipalitySubArea = locationData?.municipalitySubAreas.find(
		(m) => m.id == currentApartment!.municipalitySubAreaId,
	);
	const municipalitySubName = municipalitySubArea ? municipalitySubArea.name : '??';

	const municipality = locationData?.municipalities.find((m) => m.id == currentApartment?.municipalityId);
	const municipalityName = municipality ? municipality.name : '??';

	return (
		<h2 className="mod-rentFlow__heading -large -alignCenter">
			<span className="mod-rentFlow__heading--regular">
				{activeTab === contactRequestTabIndex
					? getResourceText('DirectRental_ContactRequest_Heading_Apartment')
					: getResourceText('DirectRental_Heading_Apartment')}
			</span>{' '}
			{currentApartment.streetAddress}, {municipalitySubName}, {municipalityName}
		</h2>
	);
}

function Tabs({ activeTab, setActiveTab }: WithActiveTab) {
	const currentApartment = useAppSelector(({ directRental: { currentApartment } }) => currentApartment);

	const firstTabClicked = () => {
		setActiveTab(0);
	};

	const secondTabClicked = () => {
		setActiveTab(1);
	};
	return (
		<div className="mod-cardTabs" role="tabpanel">
			{currentApartment?.channel === ApartmentSalesChannel.DirectRental && (
				<button
					onClick={firstTabClicked}
					className={'mod-cardTabs__tab' + (activeTab === 0 ? ' -active' : '')}
					aria-selected={activeTab === 0}
					aria-controls={directRentalTabId}
					role="tab"
				>
					<div className="mod-cardTabs__tabWrapper">
						<svg
							className="mod-cardTabs__tabIcon"
							xmlns="http://www.w3.org/2000/svg"
							width="36"
							height="46"
							viewBox="0 0 36 46"
						>
							<path
								fill="#CF3F6C"
								fillOpacity=".8"
								fillRule="nonzero"
								d="M34.32 24.5a3.54 3.54 0 00-4.89 0l-6.6 6.6a3.48 3.48 0 00-3.105-1.965H7.5a4.2 4.2 0 00-2.685.99L.51 33.71a.755.755 0 00.99 1.14l4.29-3.63a2.685 2.685 0 011.71-.63h12.225a1.98 1.98 0 010 3.945h-11.7a.75.75 0 00-.75.75c.008.41.34.742.75.75h11.7a3.465 3.465 0 003.435-3.195l7.32-7.32a2.01 2.01 0 012.79 0c.371.362.577.861.57 1.38a1.95 1.95 0 01-.57 1.395l-12 11.61a2.355 2.355 0 01-1.68.69h-9.24a4.035 4.035 0 00-2.835 1.17l-2.445 2.46a.72.72 0 000 1.05.72.72 0 001.05 0l2.46-2.445a2.52 2.52 0 011.77-.735h9.3A3.885 3.885 0 0022.38 41l12-11.625a3.465 3.465 0 000-4.905l-.06.03zM10.5 28.385l4.08-1.17 1.5-2.61-.585-2.04 2.04-.585-.57-1.98 2.04-.57.495-.93V26l3 3 3-3v-3L24 21.5l1.5-1.5-1.5-1.5 1.5-1.5v-2.355a5.91 5.91 0 00-2.25-11.01V.98a.75.75 0 10-1.5 0v2.64a6 6 0 00-3.945 2.235A5.835 5.835 0 0014.88 8.3a5.91 5.91 0 000 6.105L9.36 24.29l1.14 4.095zm13.5-12l-1.05 1.065-1.065 1.065 1.065 1.05.435.45-.435.435-1.065 1.065L24 23.585v1.755l-1.5 1.5-1.5-1.5V15.275c.427.106.865.166 1.305.18h.195a6.12 6.12 0 001.5-.21v1.14zm-2.25-11.22v1.8A1.26 1.26 0 1023.76 8c0-.4-.197-.775-.525-1.005v-1.86a4.5 4.5 0 012.55 1.335c.816.854 1.254 2 1.215 3.18A4.5 4.5 0 0122.5 14h-.165a4.5 4.5 0 01-3.135-1.5 4.5 4.5 0 012.535-7.425l.015.09zm-5.595 3.9c.135-.195.285-.36.435-.54a4.5 4.5 0 00-.09.78 5.895 5.895 0 003 5.325v1.155a4.41 4.41 0 01-1.77-.585 4.5 4.5 0 01-1.5-6.135h-.075zm-.255 6.63c.319.298.665.564 1.035.795a6 6 0 001.575.66l-.51.945-2.895.81.405 1.5.18.6-.6.18-1.5.405.405 1.5.405 1.5-.855 1.5-2.04.57-.51-2.16 4.905-8.805z"
							/>
						</svg>
						<div className="mod-cardTabs__tabText">
							<ResourceText resourceKey="DirectRental_WayFinder_Tab_DirectRental" />
						</div>
					</div>
				</button>
			)}
			<button
				onClick={secondTabClicked}
				className={'mod-cardTabs__tab' + (activeTab === 1 ? ' -active' : '')}
				aria-selected={activeTab === 1}
				aria-controls={contactRequestTabId}
			>
				<div className="mod-cardTabs__tabWrapper">
					<svg
						className="mod-cardTabs__tabIcon"
						xmlns="http://www.w3.org/2000/svg"
						width="46"
						height="42"
						viewBox="0 0 46 42"
					>
						<path
							fill="#CF3F6C"
							fillRule="nonzero"
							d="M31.012 9.33a3.589 3.589 0 00-2.416 3.388v2.116a11.224 11.224 0 01-9.294-6.463 9.808 9.808 0 01-2.316 3.318 3.632 3.632 0 00-2.245-2.302 8.579 8.579 0 0116.271 0v-.058zm14.741 31.24V42H0v-1.43h3.374l3.146-8.579a8.579 8.579 0 018.064-5.719h16.585a8.579 8.579 0 018.064 5.72l3.146 8.578h3.374zm-30.025-5.719h14.298v-7.149H15.728v7.15zm-4.29 5.72V28.46a7.149 7.149 0 00-3.574 3.975l-2.96 8.135h6.534zm29.411 0l-2.96-8.136a7.149 7.149 0 00-3.574-3.975v12.11h6.534zm-6.534-28.697c0-6.317-5.121-11.438-11.438-11.438-6.318 0-11.439 5.121-11.439 11.438v3.718a2.145 2.145 0 104.29 0v-2.86a2.145 2.145 0 00-2.145-2.187h-.6a10.009 10.009 0 0119.788 0h-.6a2.145 2.145 0 00-2.145 2.173v2.86a2.13 2.13 0 00.843 1.701 4.804 4.804 0 01-4.29 2.588h-.17a1.816 1.816 0 00-1.66-1.087h-3.302a1.816 1.816 0 000 3.632h3.346a1.816 1.816 0 001.672-1.13h.143a6.22 6.22 0 005.605-3.46v-.128a2.145 2.145 0 002.102-2.116v-3.704z"
						/>
					</svg>
					<div className="mod-cardTabs__tabText">
						<ResourceText resourceKey="DirectRental_WayFinder_Tab_ContactRequest" />
					</div>
				</div>
			</button>
			<div className="mod-cardTabs__tabShadowCenter"></div>
			<div className="mod-cardTabs__tabShadowBottom"></div>
		</div>
	);
}

function DirectRental() {
	const language = useLanguage();

	const onLinkClick = () => {
		addTelemetryEventForStep(0);
	};

	return (
		<div className="mod-cardTabs__contentTab" id={directRentalTabId} role="tabpanel">
			<div className="mod-cardTabs__badge">
				<ResourceImage resourceKey="DirectRentalWayFinder_DirectRental_Badge" />
			</div>
			<h1 className="mod-cardTabs__heading">
				<ResourceText resourceKey="DirectRental_WayFinder_Heading_RentNow" />
			</h1>
			<div className="mod-cardTabs__text">
				<ul className="atom-checkList mod-cardTabs__checkList">
					<li className="atom-checkList__item">
						<ResourceText resourceKey="DirectRental_WayFinder_Bullet_PayFirstMonthsRent" />
					</li>
					<li className="atom-checkList__item">
						<ResourceText resourceKey="DirectRental_WayFinder_Bullet_NoDeposit" />
					</li>
					<li className="atom-checkList__item">
						<ResourceText
							resourceKey="DirectRental_WayFinder_Bullet_CancelationPolicy"
							openLinksInNewTab={true}
							textType="Markdown"
						/>
					</li>
				</ul>
				<div className="atom-card__ctaGroup">
					<Link className="mod-cardTabs__cta" to={routeFromRoot(routes.rent)[language]} onClick={onLinkClick}>
						<span className="mod-cardTabs__ctaIconWrapper">
							<svg
								className="icon"
								width="28"
								height="31"
								viewBox="0 0 28 31"
								xmlns="http://www.w3.org/2000/svg"
							>
								<g transform="scale(1 -1) rotate(-45 -22.5 6.4)" fill="none" fillRule="evenodd">
									<path
										d="M28 11l-8.2 8.2A7 7 0 0011 26a7 7 0 1013.5-2.5L27 21v-3h3l3-3v-4h-5z"
										stroke="#FFF"
										strokeWidth="2"
										strokeLinecap="square"
									/>
									<circle fill="#FFF" cx="17.6" cy="26.4" r="2" />
									<path
										d="M15.3 3.1l-3 11.3a7 7 0 00-4.2 10.2 7 7 0 002.8 2.7c-.2-5.3 3.3-8.8 8.5-8.3a8.8 8.8 0 00-.8-3.3l.9-3.4L18 9.7l2.6-1.5 1-4.1-2-3.5-4.3 2.5z"
										stroke="#FFF"
										strokeWidth="2"
										strokeLinecap="square"
									/>
								</g>
							</svg>
							<ResourceText resourceKey="DirectRental_WayFinder_Button_StartRenting" />
						</span>
					</Link>
					<span className="mod-cardTabs__centerElement">
						<Link
							to={routeFromRoot(routes.companyApplication)[language]}
							className="mod-cardTabs__textLink"
						>
							<ResourceText resourceKey="DirectRental_WayFinder_RentingForACompany" />
						</Link>
					</span>
				</div>
			</div>
		</div>
	);
}

function ContactRequest() {
	const language = useLanguage();
	const { selectedApartmentId, contactRequestStartProcessingState, isDirectRentalInProgress, locationData } =
		useAppSelector(
			({
				directRental: {
					currentApartment,
					selectedApartmentId,
					contactRequestStartProcessingState,
					isDirectRentalInProgress,
				},
				locationData: { locationData },
			}) => ({
				currentApartment,
				selectedApartmentId,
				contactRequestStartProcessingState,
				isDirectRentalInProgress,
				locationData,
			}),
		);
	const dispatch = useDispatch();
	const startContactRequest = (e: React.MouseEvent) => {
		e.preventDefault();

		if (selectedApartmentId && locationData) {
			dispatch(directRentalActions.startContactRequest.request(selectedApartmentId));
		}
	};

	return (
		<div className="mod-cardTabs__contentTab" id={contactRequestTabId} role="tabpanel">
			<div className="mod-cardTabs__iconTitle">
				<svg
					className="mod-cardTabs__tabIcon -binder"
					xmlns="http://www.w3.org/2000/svg"
					width="52"
					height="60"
					viewBox="0 0 52 60"
				>
					<path
						id="mod-cardTabs__tabIcon-pathTwo"
						fill="#002663"
						d="M20 0a4.8 4.8 0 00-4.8 4.8v1.6H2.4A2.4 2.4 0 000 8.8v44.8C0 54.9 1 56 2.4 56h24.4a15 15 0 01-1-1.6H2.4a.8.8 0 01-.8-.8V8.8c0-.5.3-.8.8-.8h12.8v1.6h-.8a3.2 3.2 0 00-3.2 3.2v.8h.2a3.2 3.2 0 003 2.4h11.2c1.5 0 2.7-1 3-2.4h.2v-.8c0-1.8-1.4-3.2-3.2-3.2h-.8V8h12.8c.5 0 .8.3.8.8V32H40V8.8c0-1.3-1-2.4-2.4-2.4H24.8V4.8C24.8 2.2 22.6 0 20 0zm0 1.6c1.8 0 3.2 1.4 3.2 3.2v5.6c0 .5-.3.8-.8.8h-4.8a.8.8 0 01-.8-.8V4.8c0-1.8 1.4-3.2 3.2-3.2zM20 4a.8.8 0 100 1.6.8.8 0 000-1.6zM5.6 11.2a.8.8 0 100 1.6.8.8 0 000-1.6zm8.8 0h1c.3 1 1.2 1.6 2.2 1.6h4.8c1 0 2-.7 2.3-1.6h.9c.9 0 1.6.7 1.6 1.6 0 .9-.7 1.6-1.6 1.6H14.4c-.9 0-1.6-.7-1.6-1.6 0-.9.7-1.6 1.6-1.6zm-8.8 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zM20 24a5.6 5.6 0 00-5.6 5.6c0 2 1 3.6 2.5 4.6a8 8 0 00-4.9 7.4h1.6a6.4 6.4 0 0112.5-2l1-1.6a8 8 0 00-4-3.8 5.6 5.6 0 002.5-4.6c0-3-2.5-5.6-5.6-5.6zm0 1.6a4 4 0 014 4 4 4 0 01-4 4 4 4 0 01-4-4 4 4 0 014-4zM5.6 27.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm33.6.8c-4.8 0-9 2.7-11.2 6.7l-.3.5a12.7 12.7 0 00-1.3 5.6 12.8 12.8 0 1012.8-12.8zm0 1.6a11.2 11.2 0 110 22.4 11.2 11.2 0 010-22.4zm-33.6.8a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm39 2.6L37 50l-2.9-3-1.1 1.2 4 4 8.7-8.5-1-1.1zm-39 .6a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6zm0 3.2a.8.8 0 100 1.6.8.8 0 000-1.6z"
					/>
				</svg>
				<div className="mod-cardTabs__text">
					<p className="-ingress">
						<strong>
							<ResourceText resourceKey="DirectRental_WayFinder_ContactRequestInfo" />
						</strong>
					</p>
				</div>
			</div>

			<div className="mod-cardTabs__text">
				<ul className="atom-checkList mod-cardTabs__checkList">
					<li className="atom-checkList__item">
						<ResourceText resourceKey="DirectRental_WayFinder_Bullet_ContactRequestIdentificationInfo" />
					</li>
					<li className="atom-checkList__item">
						<ResourceText
							resourceKey="DirectRental_WayFinder_Bullet_ContactRequestCreditCheckInfo"
							openLinksInNewTab={true}
							textType="MarkdownInline"
						/>
					</li>
				</ul>
				<span className="mod-cardTabs__textLight atom-card__footNote">
					<ResourceText resourceKey="DirectRental_WayFinder_ContactRequestFootNote" />
				</span>
				<div className="atom-card__ctaGroup">
					<WaitingButton
						className="mod-cardTabs__cta"
						onClick={startContactRequest}
						disabled={isDirectRentalInProgress}
						waiting={contactRequestStartProcessingState === ApiOperationState.Requested}
					>
						<ResourceText
							resourceKey={
								isDirectRentalInProgress
									? 'ApartmentPage_RentalProcessAlreadyStarted'
									: 'DirectRental_WayFinder_Button_Continue'
							}
						/>
					</WaitingButton>

					<span className="mod-cardTabs__centerElement">
						<Link
							to={routeFromRoot(routes.companyApplication)[language]}
							className="mod-cardTabs__textLink"
						>
							<ResourceText resourceKey="DirectRental_WayFinder_RentingForACompany" />
						</Link>
					</span>
				</div>
			</div>

			{contactRequestStartProcessingState === ApiOperationState.Failed && <ErrorMessageDisplay />}
		</div>
	);
}
